import React, { Component } from "react";
import { Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import { LocalDataSetup } from "pouchDB/LocalDataSetup";

class JobForms extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            availableForms: [],
            loading: true,
            expandedCategories: {},
            fillFormModal: false,
            selectedForm: null,
            formQuestions: [],
            formAnswers: {},
            loadingQuestions: false,
            dropdownOptions: {},
            completedForms: [],
            loadingCompleted: true,
            viewMode: false,
            originalAnswers: {},
            hasChanges: false,
            currentFormDocId: null
        };
    }

    async componentDidMount() {
        await this.loadForms();
        await this.loadCompletedForms();
    }

    loadForms = async () => {
        try {
            const db_form_main_local = LocalDataSetup.db_form_main_local;
            const forms = await db_form_main_local.allDocs({
                include_docs: true
            });

            // Group forms by category and sort
            const groupedForms = {};
            forms.rows.forEach(row => {
                const category = row.doc?.category || 'Uncategorized';
                if (!groupedForms[category]) {
                    groupedForms[category] = [];
                }
                groupedForms[category].push(row);
            });

            // Sort categories alphabetically and forms within each category
            const sortedCategories = Object.keys(groupedForms).sort();
            sortedCategories.forEach(category => {
                groupedForms[category].sort((a, b) => {
                    const nameA = a.doc?.form_name || '';
                    const nameB = b.doc?.form_name || '';
                    return nameA.localeCompare(nameB);
                });
            });

            this.setState({ 
                availableForms: groupedForms,
                loading: false 
            }, () => {
                this.updateParentAvailableForms();
            });
        } catch (error) {
            console.error('Error loading forms:', error);
            this.setState({ loading: false });
        }
    }

    handleFormSelect = async (form) => {
        console.log('Selected form:', form);
        this.setState({ 
            selectedForm: form, 
            fillFormModal: true,
            loadingQuestions: true,
            formAnswers: {},
            viewMode: false
        });
        
        await this.loadFormQuestions(form.id);
        await this.loadDropdownOptions(form.id);
    }

    loadFormQuestions = async (formId) => {
        try {
            const db_form_question_local = LocalDataSetup.db_form_question_local;
            const questions = await db_form_question_local.allDocs({
                include_docs: true
            });

            console.log('All questions loaded:', questions.rows);
            console.log('Looking for formId:', formId, 'type:', typeof formId);

            // Filter and extract questions for this specific form
            const formQuestions = questions.rows
                .filter(row => {
                    console.log('Checking row:', row.doc?.main_id, 'type:', typeof row.doc?.main_id, 'against formId:', formId);
                    return row.doc?.main_id === formId || row.doc?.main_id === formId.toString();
                })
                .flatMap(row => {
                    // Extract questions from the questions object
                    if (row.doc?.questions) {
                        return Object.values(row.doc.questions);
                    }
                    return [];
                })
                .sort((a, b) => (parseInt(a.position) || 0) - (parseInt(b.position) || 0));

            console.log('Filtered questions:', formQuestions);

            this.setState({ 
                formQuestions,
                loadingQuestions: false 
            });
        } catch (error) {
            console.error('Error loading form questions:', error);
            this.setState({ loadingQuestions: false });
        }
    }

    loadDropdownOptions = async (formId) => {
        try {
            const db_form_dropdown_local = LocalDataSetup.db_form_dropdown_local;
            const dropdowns = await db_form_dropdown_local.allDocs({
                include_docs: true
            });

            console.log('All dropdown options loaded:', dropdowns.rows);

            // Filter dropdown options for this specific form and group by question ID
            const dropdownOptions = {};
            dropdowns.rows
                .filter(row => row.doc?.main_id === formId || row.doc?.main_id === formId.toString())
                .forEach(row => {
                    const qId = row.doc.q_id;
                    if (!dropdownOptions[qId]) {
                        dropdownOptions[qId] = [];
                    }
                    dropdownOptions[qId].push({
                        value: row.doc.value,
                        position: parseInt(row.doc.position) || 0
                    });
                });

            // Sort options by position within each question
            Object.keys(dropdownOptions).forEach(qId => {
                dropdownOptions[qId].sort((a, b) => a.position - b.position);
            });

            console.log('Grouped dropdown options:', dropdownOptions);

            this.setState({ dropdownOptions });
        } catch (error) {
            console.error('Error loading dropdown options:', error);
        }
    }

    handleAnswerChange = (questionId, value) => {
        this.setState(prevState => {
            const newAnswers = {
                ...prevState.formAnswers,
                [questionId]: value
            };
            
            // Check if there are changes when in view mode
            const hasChanges = prevState.viewMode && 
                JSON.stringify(newAnswers) !== JSON.stringify(prevState.originalAnswers);
            
            return {
                formAnswers: newAnswers,
                hasChanges
            };
        });
    }

    handleFormSubmit = async () => {
        // Validate required fields
        const missingRequired = this.validateRequiredFields();
        if (missingRequired.length > 0) {
            window.alert(`Please fill in the following required fields:\n${missingRequired.join('\n')}`);
            return;
        }

        try {
            await this.saveCompletedForm();
            await this.loadCompletedForms(); // Refresh completed forms list and update parent
            this.setState({ fillFormModal: false });
        } catch (error) {
            console.error('Error saving form:', error);
            window.alert('Error saving form. Please try again.');
        }
    }

    validateRequiredFields = () => {
        const missingFields = [];
        this.state.formQuestions.forEach(question => {
            if (question.required === "1" || question.required === 1) {
                const answer = this.state.formAnswers[question.id];
                if (!answer || answer.toString().trim() === '') {
                    missingFields.push(question.question);
                }
            }
        });
        return missingFields;
    }

    saveCompletedForm = async () => {
        const db_form_results_local = LocalDataSetup.db_form_results_local;
        const timestamp = new Date().toISOString();
        const formResult = {
            _id: `${this.state.selectedForm.id}_${Date.now()}`,
            form_id: this.state.selectedForm.id,
            form_name: this.state.selectedForm.form_name,
            answers: this.state.formAnswers,
            completed_at: timestamp,
            user_id: sessionStorage.getItem('user_id') || 'unknown',
            session_id: this.props.selectedJob?.SessionID || null,
            job_id: this.props.selectedJob?.JobID || null
        };

        await db_form_results_local.put(formResult);
        console.log('Form saved successfully:', formResult);
    }

    updateCompletedForm = async () => {
        // Validate required fields
        const missingRequired = this.validateRequiredFields();
        if (missingRequired.length > 0) {
            window.alert(`Please fill in the following required fields:\n${missingRequired.join('\n')}`);
            return;
        }

        try {
            const db_form_results_local = LocalDataSetup.db_form_results_local;
            const existingDoc = await db_form_results_local.get(this.state.currentFormDocId);
            
            const updatedDoc = {
                ...existingDoc,
                answers: this.state.formAnswers,
                updated_at: new Date().toISOString()
            };

            await db_form_results_local.put(updatedDoc);
            
            // Update the original answers to reflect the new state and close modal
            this.setState({ 
                originalAnswers: { ...this.state.formAnswers },
                hasChanges: false,
                fillFormModal: false
            });
            
            await this.loadCompletedForms(); // Refresh completed forms list and update parent
        } catch (error) {
            console.error('Error updating form:', error);
            window.alert('Error updating form. Please try again.');
        }
    }

    loadCompletedForms = async () => {
        try {
            const db_form_results_local = LocalDataSetup.db_form_results_local;
            const completed = await db_form_results_local.allDocs({
                include_docs: true
            });

            this.setState({ 
                completedForms: completed.rows,
                loadingCompleted: false 
            }, () => {
                this.updateParentCompletedForms();
            });
        } catch (error) {
            console.error('Error loading completed forms:', error);
            this.setState({ loadingCompleted: false });
        }
    }

    updateParentCompletedForms = () => {
        if (this.props.onCompletedFormsChange) {
            const completedFormIds = this.state.completedForms.map(formRow => formRow.doc?.form_id).filter(Boolean);
            this.props.onCompletedFormsChange(completedFormIds);
        }
    }

    updateParentAvailableForms = () => {
        if (this.props.onAvailableFormsChange) {
            // Extract all form IDs from all categories in availableForms
            const availableFormIds = [];
            Object.keys(this.state.availableForms).forEach(category => {
                this.state.availableForms[category].forEach(formRow => {
                    if (formRow.doc?.id) {
                        availableFormIds.push(formRow.doc.id.toString());
                    }
                });
            });
            this.props.onAvailableFormsChange(availableFormIds);
        }
    }

    handleModalClose = () => {
        if (this.state.hasChanges && !this.state.viewMode) {
            const confirmClose = window.confirm(
                "You have unsaved changes that will be lost if you close this form.\n\nAre you sure you want to close without saving?"
            );
            if (!confirmClose) {
                return;
            }
        }
        this.closeFillFormModal();
    }

    closeFillFormModal = () => {
        this.setState({ 
            fillFormModal: false,
            selectedForm: null,
            formQuestions: [],
            formAnswers: {},
            dropdownOptions: {},
            viewMode: false,
            originalAnswers: {},
            hasChanges: false,
            currentFormDocId: null
        });
    }

    viewCompletedForm = async (completedForm, docId) => {
        console.log('Viewing completed form:', completedForm);
        
        // Set the selected form and answers from the completed form
        const selectedForm = {
            id: completedForm.form_id,
            form_name: completedForm.form_name
        };
        
        const answers = completedForm.answers || {};
        
        this.setState({ 
            selectedForm,
            fillFormModal: true,
            loadingQuestions: true,
            formAnswers: answers,
            originalAnswers: { ...answers }, // Store original answers for comparison
            viewMode: true,
            hasChanges: false,
            currentFormDocId: docId
        });
        
        await this.loadFormQuestions(completedForm.form_id);
        await this.loadDropdownOptions(completedForm.form_id);
    }

    deleteCompletedForm = async (completedForm, docId) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete this completed form?\n\nForm: ${completedForm.form_name}\nCompleted: ${new Date(completedForm.completed_at).toLocaleString()}\n\nThis action cannot be undone.`
        );

        if (confirmDelete) {
            try {
                const db_form_results_local = LocalDataSetup.db_form_results_local;
                const doc = await db_form_results_local.get(docId);
                await db_form_results_local.remove(doc);
                
                // Refresh the completed forms list and update parent
                await this.loadCompletedForms();
                window.alert('Form deleted successfully.');
            } catch (error) {
                console.error('Error deleting completed form:', error);
                window.alert('Error deleting form. Please try again.');
            }
        }
    }

    toggleCategory = (category) => {
        this.setState(prevState => ({
            expandedCategories: {
                ...prevState.expandedCategories,
                [category]: !prevState.expandedCategories[category]
            }
        }));
    }

    isFormRequired = (formId) => {
        try {
            const requiredAttributes = this.props.selectedJob?.RequiredAttributes;
            if (!requiredAttributes) return false;
            
            const parsed = JSON.parse(requiredAttributes);
            if (Array.isArray(parsed) && parsed.length > 0) {
                const attributeMap = parsed[0];
                return attributeMap[formId?.toString()] === -1;
            }
            return false;
        } catch (error) {
            console.error('Error parsing RequiredAttributes:', error);
            return false;
        }
    }

    isCategoryRequired = (categoryForms) => {
        return categoryForms.some(formRow => this.isFormRequired(formRow.doc?.id));
    }

    isRequiredFormCompleted = (formId) => {
        return this.state.completedForms.some(completedForm => 
            completedForm.doc?.form_id === formId
        );
    }

    render() {
        const { loading, availableForms } = this.state;

        if (loading) {
            return <div>Loading forms...</div>;
        }

        const hasCategories = Object.keys(availableForms).length > 0;

        return (
            <div>
                <h6>Completed Forms</h6>
                {this.state.loadingCompleted ? (
                    <div>Loading completed forms...</div>
                ) : (
                    <Table striped style={{ marginBottom: '30px' }}>
                        <thead>
                            <tr>
                                <th>Form Name</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.completedForms.length > 0 ? (
                                this.state.completedForms.map((formRow, index) => (
                                    <tr key={formRow.id || index}>
                                        <td>{formRow.doc?.form_name || 'N/A'}</td>
                                        <td>
                                            <Button
                                                color="info"
                                                size="sm"
                                                className="btn-round"
                                                onClick={() => this.viewCompletedForm(formRow.doc, formRow.id)}
                                                style={{ marginRight: '5px' }}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                className="btn-round"
                                                onClick={() => this.deleteCompletedForm(formRow.doc, formRow.id)}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="2" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                        No completed forms
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
                <h6>Required Forms</h6>
                {hasCategories ? (
                    <Table style={{ marginBottom: '30px' }}>
                        {/* <thead>
                            <tr>
                                <th>Form Name</th>
                                <th>Action</th>
                            </tr>
                        </thead> */}
                        <tbody>
                            {(() => {
                                // Get all required forms from all categories
                                const requiredForms = [];
                                Object.keys(availableForms).forEach(category => {
                                    availableForms[category].forEach(formRow => {
                                        if (this.isFormRequired(formRow.doc?.id)) {
                                            requiredForms.push(formRow);
                                        }
                                    });
                                });
                                
                                return requiredForms.length > 0 ? (
                                    requiredForms.map((formRow, index) => {
                                        const isCompleted = this.isRequiredFormCompleted(formRow.doc?.id);
                                        const rowStyle = {
                                            backgroundColor: isCompleted ? '#d4edda' : '#f8d7da'
                                        };
                                        
                                        return (
                                            <tr key={formRow.id || `required-${index}`} style={rowStyle}>
                                                <td>{formRow.doc?.form_name || 'N/A'}</td>
                                                <td>
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        className="btn-round"
                                                        onClick={() => this.handleFormSelect(formRow.doc)}
                                                    >
                                                        {isCompleted ? 'View/Edit Form' : 'Start Form'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="2" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                            No required forms for this job
                                        </td>
                                    </tr>
                                );
                            })()}
                        </tbody>
                    </Table>
                ) : (
                    <div style={{ textAlign: 'center', fontStyle: 'italic', marginBottom: '30px' }}>
                        No forms available
                    </div>
                )}
                
                <h6>Available Forms</h6>
                <Table striped>
                    {/* <thead>
                        <tr>
                            <th>Form ID</th>
                            <th>Form Name</th>
                            <th>Action</th>
                        </tr>
                    </thead> */}
                    <tbody>
                        {hasCategories ? (
                            Object.keys(availableForms).sort().map(category => {
                                const isExpanded = this.state.expandedCategories[category];
                                const formCount = availableForms[category].length;
                                
                                return (
                                    <React.Fragment key={category}>
                                        <tr 
                                            style={{ 
                                                backgroundColor: '#f8f9fa',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => this.toggleCategory(category)}
                                        >
                                            <td colSpan="3" style={{ 
                                                fontWeight: 'bold', 
                                                fontSize: '1.1em', 
                                                padding: '12px 8px',
                                                borderTop: '2px solid #dee2e6'
                                            }}>
                                                <i 
                                                    className={`now-ui-icons arrows-1_minimal-${isExpanded ? 'down' : 'right'}`}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                {category} ({formCount} form{formCount !== 1 ? 's' : ''})
                                            </td>
                                        </tr>
                                        {isExpanded && availableForms[category].map((formRow, index) => (
                                            <tr key={formRow.id || `${category}-${index}`}>
                                                {/* <td>{formRow.doc?.id || 'N/A'}</td> */}
                                                <td>
                                                    {formRow.doc?.form_name || 'N/A'}
                                                </td>
                                                <td>
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        className="btn-round"
                                                        onClick={() => this.handleFormSelect(formRow.doc)}
                                                    >
                                                        Start Form
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="3" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                    No forms available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {/* Form Filling Modal */}
                <Modal isOpen={this.state.fillFormModal} size="lg" toggle={this.handleModalClose}>
                    <ModalHeader toggle={this.handleModalClose}>
                        <b>{this.state.selectedForm?.form_name}</b>
                    </ModalHeader>
                    <ModalBody>
                        {this.state.loadingQuestions ? (
                            <div>Loading questions...</div>
                        ) : (
                            <div>
                                {this.state.formQuestions.length > 0 ? (
                                    this.state.formQuestions.map((question, index) => {
                                        const inputType = question.input_type?.toLowerCase();
                                        const isDisplayOnly = ['paragraph', 'header', 'hyperlink'].includes(inputType);
                                        
                                        return (
                                            <FormGroup key={question.id}>
                                                {!isDisplayOnly && (
                                                    <Label>
                                                        {question.question}
                                                        {question.required === "1" && <span style={{color: 'red'}}> *</span>}
                                                    </Label>
                                                )}
                                                {this.renderQuestionInput(question)}
                                            </FormGroup>
                                        );
                                    })
                                ) : (
                                    <div style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                        No questions found for this form
                                    </div>
                                )}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button 
                            color="danger" 
                            className="btn-round" 
                            onClick={this.handleModalClose}
                        >
                            {this.state.viewMode ? 'Close' : 'Cancel'}
                        </Button>
                        {!this.state.viewMode && (
                            <Button 
                                color="success" 
                                className="btn-round"
                                onClick={this.handleFormSubmit}
                                disabled={this.state.loadingQuestions}
                            >
                                Submit Form
                            </Button>
                        )}
                        {this.state.viewMode && this.state.hasChanges && (
                            <Button 
                                color="success" 
                                className="btn-round"
                                onClick={this.updateCompletedForm}
                                disabled={this.state.loadingQuestions}
                            >
                                Update Form
                            </Button>
                        )}
                    </ModalFooter>
                </Modal>
            </div>
        );
    }

    renderQuestionInput = (question) => {
        const questionId = question.id;
        const currentValue = this.state.formAnswers[questionId] || '';

        // Basic input rendering - can be expanded based on question type
        switch (question.input_type?.toLowerCase()) {
            case 'textarea':
                return (
                    <Input
                        type="textarea"
                        rows="3"
                        value={currentValue}
                        onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                    />
                );
            case 'select':
            case 'dropdown':
                const options = this.state.dropdownOptions[questionId] || [];
                return (
                    <Input
                        type="select"
                        value={currentValue}
                        onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                    >
                        <option value="">Select an option</option>
                        {options.map((option, index) => (
                            <option key={index} value={option.value}>
                                {option.value}
                            </option>
                        ))}
                    </Input>
                );
            case 'checkbox':
                return (
                    <Input
                        type="checkbox"
                        checked={currentValue === 'true' || currentValue === true}
                        onChange={(e) => this.handleAnswerChange(questionId, e.target.checked)}
                    />
                );
            case 'number':
                return (
                    <Input
                        type="number"
                        value={currentValue}
                        onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                    />
                );
            case 'date':
                return (
                    <Input
                        type="date"
                        value={currentValue}
                        onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                    />
                );
            case 'yes/no':
            case 'yesno':
                return (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Label 
                            check 
                            style={{
                                backgroundColor: currentValue === 'yes' ? '#28a745' : '#f8f9fa',
                                color: currentValue === 'yes' ? 'white' : '#333',
                                border: '2px solid #28a745',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                minWidth: '80px',
                                textAlign: 'center'
                            }}
                        >
                            <Input
                                type="radio"
                                name={`radio-${questionId}`}
                                value="yes"
                                checked={currentValue === 'yes'}
                                onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                                style={{ display: 'none' }}
                            />
                            Yes
                        </Label>
                        <Label 
                            check 
                            style={{
                                backgroundColor: currentValue === 'no' ? '#dc3545' : '#f8f9fa',
                                color: currentValue === 'no' ? 'white' : '#333',
                                border: '2px solid #dc3545',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                minWidth: '80px',
                                textAlign: 'center'
                            }}
                        >
                            <Input
                                type="radio"
                                name={`radio-${questionId}`}
                                value="no"
                                checked={currentValue === 'no'}
                                onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                                style={{ display: 'none' }}
                            />
                            No
                        </Label>
                        <Label 
                            check 
                            style={{
                                backgroundColor: currentValue === 'na' ? '#6c757d' : '#f8f9fa',
                                color: currentValue === 'na' ? 'white' : '#333',
                                border: '2px solid #6c757d',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                minWidth: '80px',
                                textAlign: 'center'
                            }}
                        >
                            <Input
                                type="radio"
                                name={`radio-${questionId}`}
                                value="na"
                                checked={currentValue === 'na'}
                                onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                                style={{ display: 'none' }}
                            />
                            N/A
                        </Label>
                    </div>
                );
            case 'header':
                return (
                    <>
                        <hr />
                            <Label><b>{question.question}</b></Label>
                        <hr />
                    </>
                );
            case 'paragraph':
                return (
                    <p>{question.question}</p>
                );
            case 'hyperlink':
                return (
                    <a href={question.question} target="_blank" rel="noopener noreferrer">{question.question}</a>
                );
            default:
                return (
                    <Input
                        type="text"
                        value={currentValue}
                        onChange={(e) => this.handleAnswerChange(questionId, e.target.value)}
                    />
                );
        }
    }
}

export default JobForms;